import React, { FunctionComponent, useState } from "react";

import { useTranslation } from "react-i18next";

import { faBookmark, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import { classNames } from "@/utils/classNames";

import {
	useCreateSearchProfilePresetMutation,
	useDeleteSearchProfilePresetMutation,
	useGetSearchProfilePresetsQuery,
	useUpdateSearchProfilePresetMutation,
} from "@/pages/Private/redux/searchProfilePreset/searchProfilePreset.api";
import { SearchProfilePreset } from "@/pages/Private/redux/searchProfilePreset/searchProfilePreset.schema";

import { EnrichmentFilterConfig } from "../../ListEditDetails";

/** Shape stored in a preset's opaque `config` JSON. */
interface SearchProfilePresetConfig {
	enrichmentFilterConfig?: EnrichmentFilterConfig;
	maxPerCompany?: number;
}

interface SearchProfilePresetBarProps {
	titleFilters?: EnrichmentFilterConfig;
	setTitleFilters: React.Dispatch<React.SetStateAction<EnrichmentFilterConfig | undefined>>;
	maxPerCompany?: number;
	setMaxPerCompany: (maxPerCompany: number) => void;
	/**
	 * A chip before the saved presets that loads the list's own search profiles, selected at
	 * first: the bulk find person dialog starts from them (PREC-4398). Left out, the bar is as the
	 * Find person step shows it.
	 */
	listProfiles?: { label: string; onSelect: () => void };
	/**
	 * The people per company Clear resets to: the step's default, unless the host starts from its
	 * own (the bulk dialog keeps its starting value, 1 on a tracker, PREC-4398).
	 */
	clearedMaxPerCompany?: number;
}

const DEFAULT_MAX_PER_COMPANY = 3;

/** The selection of the `listProfiles` chip; a saved preset is selected by its id. */
const LIST_PROFILES = "LIST_PROFILES";

const chipClassName = (isSelected: boolean): string =>
	classNames(
		"inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-semibold cursor-pointer transition-colors",
		isSelected
			? "bg-blue-action text-white border-blue-action"
			: "bg-white text-text-meta border-border-light hover:border-brand-light"
	);

/**
 * Save / load / update / clear named Find-Person waterfall presets. Loading a preset populates the
 * current waterfall + people-per-company; "Update" writes the current editor state back to the
 * selected preset; "Save as new" persists it under a new name; "Clear" resets the editor.
 */
export const SearchProfilePresetBar: FunctionComponent<SearchProfilePresetBarProps> = ({
	titleFilters,
	setTitleFilters,
	maxPerCompany,
	setMaxPerCompany,
	listProfiles,
	clearedMaxPerCompany = DEFAULT_MAX_PER_COMPANY,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`defineIcps.presets.${key}`);

	const { data } = useGetSearchProfilePresetsQuery();
	const [createPreset, { isLoading: isCreating }] = useCreateSearchProfilePresetMutation();
	const [updatePreset] = useUpdateSearchProfilePresetMutation();
	const [deletePreset] = useDeleteSearchProfilePresetMutation();

	const [selectedId, setSelectedId] = useState<number | typeof LIST_PROFILES | null>(
		listProfiles ? LIST_PROFILES : null
	);
	const [isNaming, setIsNaming] = useState(false);
	const [newName, setNewName] = useState("");

	const presets = data?.data ?? [];

	const buildConfig = (): Record<string, unknown> => ({
		enrichmentFilterConfig: titleFilters ?? {},
		maxPerCompany: maxPerCompany ?? DEFAULT_MAX_PER_COMPANY,
	});

	const handleLoad = (preset: SearchProfilePreset) => {
		const config = preset.config as unknown as SearchProfilePresetConfig;

		setTitleFilters(config.enrichmentFilterConfig ?? {});

		if (config.maxPerCompany) {
			setMaxPerCompany(config.maxPerCompany);
		}

		setSelectedId(preset.id);
	};

	const handleSaveNew = async () => {
		const name = newName.trim();

		if (!name) {
			return;
		}

		const created = await createPreset({ name, config: buildConfig() }).unwrap();

		setSelectedId(created.id);
		setIsNaming(false);
		setNewName("");
	};

	const handleUpdate = async () => {
		if (typeof selectedId !== "number") {
			return;
		}

		await updatePreset({ id: selectedId, config: buildConfig() }).unwrap();
	};

	const handleClear = () => {
		setTitleFilters({});
		setMaxPerCompany(clearedMaxPerCompany);
		setSelectedId(null);
	};

	const handleDelete = async (event: React.MouseEvent, id: number) => {
		event.stopPropagation();

		await deletePreset(id).unwrap();

		if (selectedId === id) {
			setSelectedId(null);
		}
	};

	return (
		<div className="bg-white border border-border-light rounded-lg p-5 mb-5">
			<div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
				<div>
					<div className="text-[15px] font-semibold text-text">{ts("title")}</div>
					<div className="text-xs text-gray-700 mt-0.5">{ts("subtitle")}</div>
				</div>
				<div className="flex items-center gap-2 flex-wrap">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={typeof selectedId !== "number"}
						size={ButtonSize.XS}
						title={ts("update")}
						onClick={() => void handleUpdate()}
					/>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						image={<Icon className="w-3.5 h-3.5" icon={faBookmark} />}
						size={ButtonSize.XS}
						title={ts("saveAsNew")}
						onClick={() => setIsNaming(true)}
					/>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.XS}
						title={ts("clear")}
						onClick={handleClear}
					/>
				</div>
			</div>

			{isNaming && (
				<div className="flex items-center gap-2 mb-3">
					<InputField
						handleChange={(event) => setNewName(event.target.value)}
						name="presetName"
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								void handleSaveNew();
							}
						}}
						placeholder={ts("namePlaceholder")}
						size={ButtonSize.XS}
						value={newName}
					/>
					<Button
						color={ButtonColor.ACTION}
						disabled={!newName.trim() || isCreating}
						size={ButtonSize.XS}
						title={ts("save")}
						onClick={() => void handleSaveNew()}
					/>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.XS}
						title={ts("cancel")}
						onClick={() => {
							setIsNaming(false);
							setNewName("");
						}}
					/>
				</div>
			)}

			<div className="flex items-center gap-2 flex-wrap">
				{listProfiles && (
					<button
						className={chipClassName(selectedId === LIST_PROFILES)}
						type="button"
						onClick={() => {
							listProfiles.onSelect();
							setSelectedId(LIST_PROFILES);
						}}
					>
						{listProfiles.label}
					</button>
				)}
				{presets.length === 0 && <span className="text-xs text-text-meta">{ts("empty")}</span>}
				{presets.map((preset) => {
					const isSelected = selectedId === preset.id;

					return (
						<div
							key={preset.id}
							className={chipClassName(isSelected)}
							onClick={() => handleLoad(preset)}
						>
							{preset.name}
							<Icon
								className={classNames(
									"w-3 h-3 cursor-pointer",
									isSelected ? "opacity-80 hover:opacity-100" : "opacity-50 hover:opacity-100"
								)}
								icon={faXmark}
								onClick={(event: React.MouseEvent) => void handleDelete(event, preset.id)}
							/>
						</div>
					);
				})}
			</div>
		</div>
	);
};
